import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import { change, formValueSelector } from 'redux-form';
import { useTranslation } from 'react-i18next';
import { Grid, Field } from 'nallian-shared-ui/lib/components';
import { SelectFieldValueComponent } from 'nallian-shared-ui/lib/components/redux-form-semantic-ui';
import { FormModal } from 'nallian-shared-ui/lib/modules';
import { generateErrorsForRequiredFields } from '../../../app/lib/validationHelper';
import PrincipalAutoCompleteValueComponent from '../../../app/components/PrincipalAutoCompleteValueComponent';
import createRoleAssignmentService from '../../services/createRoleAssignmentService';
import roleCommunitiesService from '../../services/roleCommunitiesService';
import roleOrganisationsService from '../../services/roleOrganisationsService';

const FORM_NAME = 'addRoleAssignment';
const REQUIRED_FIELDS = ['communityId', 'organisationId', 'principalId'];
const selector = formValueSelector(FORM_NAME);

const validate = values => {
  let errors = {};
  errors = generateErrorsForRequiredFields(REQUIRED_FIELDS, values, errors);
  return errors;
};

const AddRoleAssignmentModal = ({
  appId,
  installationId,
  featureId,
  roleId,
  isGlobal,
  resource,
  onClose,
  dispatchCreateRoleAssignmentPost,
  createRoleAssignmentFetch,
  dispatchRoleCommunitiesGet,
  roleCommunitiesFetch,
  dispatchRoleOrganisationsGet,
  roleOrganisationsFetch,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const communityId = useSelector(state => selector(state, 'communityId'));
  const organisationId = useSelector(state => selector(state, 'organisationId'));
  const principalId = useSelector(state => selector(state, 'principalId'));

  useEffect(() => {
    dispatchRoleCommunitiesGet(appId, installationId, featureId, roleId);
  }, [dispatchRoleCommunitiesGet, appId, installationId, featureId, roleId]);

  const communities = useMemo(
    () =>
      (roleCommunitiesFetch.value || []).map(c => ({
        key: c.id,
        value: c.id,
        text: c.name,
      })),
    [roleCommunitiesFetch.value]
  );

  const organisations = useMemo(
    () =>
      (roleOrganisationsFetch.value || []).map(o => ({
        key: o.id,
        value: o.id,
        text: o.name,
      })),
    [roleOrganisationsFetch.value]
  );

  const handleCommunityChange = useCallback(
    (_, newValue) => {
      dispatch(change(FORM_NAME, 'organisationId', null));
      dispatch(change(FORM_NAME, 'principalId', null));
      if (newValue) dispatchRoleOrganisationsGet(appId, installationId, featureId, roleId, newValue);
    },
    [dispatch, dispatchRoleOrganisationsGet, appId, installationId, featureId, roleId]
  );

  const handleOrganisationChange = useCallback(() => {
    dispatch(change(FORM_NAME, 'principalId', null));
  }, [dispatch]);

  const handleSubmit = values => {
    dispatchCreateRoleAssignmentPost(
      appId,
      installationId,
      featureId,
      roleId,
      {
        communityId: isGlobal ? null : values.communityId,
        principalId: values.principalId,
      },
      onClose,
      undefined,
      resource,
      t
    );
  };

  return (
    <FormModal
      closeIcon
      header={t('addUser')}
      dataLoading={false}
      submitProcessing={createRoleAssignmentFetch.pending}
      onSubmit={handleSubmit}
      onCancel={onClose}
      data={{}}
      t={t}
      validate={validate}
      form={FORM_NAME}
      cancelButtonContent={t('cancel')}
      saveButtonIcon="plus"
      saveButtonContent={t('addUser')}
      saveDisabled={!communityId || !organisationId || !principalId}
    >
      <Grid>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="communityId"
            valueComponent={SelectFieldValueComponent}
            label={t('community')}
            options={communities}
            loading={roleCommunitiesFetch.pending}
            disabled={createRoleAssignmentFetch.pending || roleCommunitiesFetch.pending}
            onChange={handleCommunityChange}
            search
            fluid
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="organisationId"
            valueComponent={SelectFieldValueComponent}
            label={t('organisation')}
            options={organisations}
            loading={roleOrganisationsFetch.pending}
            disabled={
              !communityId || createRoleAssignmentFetch.pending || roleOrganisationsFetch.pending
            }
            onChange={handleOrganisationChange}
            search
            fluid
          />
        </Grid.Column>
        <Grid.Column mobile={16} tablet={16} computer={16}>
          <Field
            name="principalId"
            valueComponent={PrincipalAutoCompleteValueComponent}
            label={t('principalName')}
            hideLabel
            organisationId={organisationId || ''}
            placeholder={t('searchForNameOrEmailOfUser')}
            disabled={!organisationId || createRoleAssignmentFetch.pending}
          />
        </Grid.Column>
      </Grid>
    </FormModal>
  );
};

AddRoleAssignmentModal.propTypes = {
  appId: PropTypes.string.isRequired,
  installationId: PropTypes.string.isRequired,
  featureId: PropTypes.string.isRequired,
  roleId: PropTypes.string.isRequired,
  isGlobal: PropTypes.bool,
  resource: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  dispatchCreateRoleAssignmentPost: PropTypes.func.isRequired,
  createRoleAssignmentFetch: PropTypes.object.isRequired,
  dispatchRoleCommunitiesGet: PropTypes.func.isRequired,
  roleCommunitiesFetch: PropTypes.object.isRequired,
  dispatchRoleOrganisationsGet: PropTypes.func.isRequired,
  roleOrganisationsFetch: PropTypes.object.isRequired,
};

AddRoleAssignmentModal.defaultProps = { isGlobal: false };

export default compose(
  createRoleAssignmentService,
  roleCommunitiesService,
  roleOrganisationsService
)(AddRoleAssignmentModal);
